/**
 * @brief 各 provider 模型列表拉取工具
 * @note 调各 provider 的 /models 或 /v1/models 接口；需要先填好 API Key
 */

import type { LlmProvider } from '@/lib/commands';

const PROVIDER_MODELS_DEFAULTS: Record<LlmProvider, string[]> = {
  openai: ['gpt-4o', 'gpt-4o-mini', 'gpt-4-turbo', 'gpt-3.5-turbo'],
  anthropic: ['claude-3-5-sonnet-latest', 'claude-3-5-haiku-latest', 'claude-3-opus-latest'],
  openrouter: ['openai/gpt-4o', 'openai/gpt-4o-mini', 'anthropic/claude-3-haiku'],
  deepseek: ['deepseek-chat', 'deepseek-coder'],
  'doubao-ark': ['doubao-pro-32k', 'doubao-pro-128k', 'doubao-lite-32k'],
};

interface ProviderConfig {
  baseURL?: string;
  apiKey?: string;
  models?: string[];
}

const PROVIDER_BASE_URLS: Record<LlmProvider, string> = {
  openai: 'https://api.openai.com',
  anthropic: 'https://api.anthropic.com',
  openrouter: 'https://openrouter.ai',
  deepseek: 'https://api.deepseek.com',
  'doubao-ark': 'https://ark.cn-beijing.volces.com/api/v3',
};

export const fetchModels = async (
  provider: LlmProvider,
  apiKey: string,
  baseURL?: string
): Promise<string[]> => {
  const url = baseURL || PROVIDER_BASE_URLS[provider];

  if (provider === 'openai' || provider === 'deepseek') {
    const res = await fetch(`${url}/v1/models`, {
      headers: { Authorization: `Bearer ${apiKey}` },
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    const models = (data.data as { id: string }[])
      .map((m) => m.id)
      .filter((id) => !id.startsWith('ft:') && !id.includes('-emb-') && !id.includes('vision'))
      .slice(0, 30);
    if (provider === 'deepseek') {
      return models.map((id) => {
        if (id === 'deepseek-v4-flash') return 'deepseek-chat';
        if (id === 'deepseek-v4-pro') return 'deepseek-pro';
        return id;
      });
    }
    return models;
  }

  if (provider === 'anthropic') {
    const res = await fetch(`${url}/v1/models`, {
      headers: { 'x-api-key': apiKey, 'anthropic-version': '2023-06-01' },
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    return (data.models as { name: string }[]).map((m) => m.name).slice(0, 30);
  }

  if (provider === 'openrouter') {
    const res = await fetch(`${url}/api/v1/models`, {
      headers: { Authorization: `Bearer ${apiKey}` },
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    return (data.data as { id: string }[]).map((m) => m.id).slice(0, 30);
  }

  if (provider === 'doubao-ark') {
    const res = await fetch(`${url}/models`, {
      headers: { Authorization: `Bearer ${apiKey}` },
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    return (data.data as { id: string }[]).map((m) => m.id).slice(0, 30);
  }

  return PROVIDER_MODELS_DEFAULTS[provider] || [];
};

export type ProviderStore = Partial<Record<LlmProvider, ProviderConfig>>;

export const LS_PROVIDER_KEY = 'rtc-aigc-console-providers';

export const loadProviderStore = (): ProviderStore => {
  try {
    const raw = localStorage.getItem(LS_PROVIDER_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

export const saveProviderStore = (store: ProviderStore) => {
  try {
    localStorage.setItem(LS_PROVIDER_KEY, JSON.stringify(store));
  } catch {
    /* noop */
  }
};

export const DEFAULT_MODELS = PROVIDER_MODELS_DEFAULTS;
